import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme, corporateLabels } from '../themes/corporate';

const TimelineContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 3rem;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: ${corporateTheme.fontFamily};
`;

const TimelineWrapper = styled.div`
  position: relative;
  padding: 2rem 0;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 3px;
  background: ${corporateTheme.primaryGradient};
  transform: translateX(-50%);
  
  @media (max-width: 768px) {
    left: 30px;
  }
`;

const TimelineItem = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: 4rem;
  position: relative;
  
  &:nth-child(even) {
    flex-direction: row-reverse;
  }
  
  @media (max-width: 768px) {
    flex-direction: row;
    margin-left: 60px;
    
    &:nth-child(even) {
      flex-direction: row;
    }
  }
`;

const TimelineContent = styled(motion.div)`
  flex: 1;
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 2.5rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  margin: 0 2rem;
  
  @media (max-width: 768px) {
    margin: 0 1rem 0 0;
  }
`;

const TimelineDot = styled(motion.div)`
  width: 80px;
  height: 80px;
  background: ${props => props.color || corporateTheme.primaryGradient};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.background};
  z-index: 10;
  box-shadow: 0 0 20px rgba(37, 99, 235, 0.3);
  font-family: ${corporateTheme.fontFamily};
  letter-spacing: 0.5px;
  text-align: center;
  line-height: 1;
  
  @media (max-width: 768px) {
    position: absolute;
    left: -90px;
    width: 60px;
    height: 60px;
    font-size: 0.6rem;
  }
`;

const PhaseTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const PhaseDate = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.textSecondary};
  margin-bottom: 1.5rem;
  font-weight: ${corporateTheme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${corporateTheme.fontFamily};
`;

const PhaseDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  font-size: 1rem;
  margin-bottom: 1.5rem;
  font-family: ${corporateTheme.fontFamily};
`;

const MilestoneList = styled.ul`
  list-style: none;
  padding: 0;
`;

const Milestone = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.8rem;
  font-size: 0.95rem;
  color: ${corporateTheme.textSecondary};
  font-family: ${corporateTheme.fontFamily};
  
  &:before {
    content: "→";
    color: ${corporateTheme.primary};
    font-weight: bold;
    margin-right: 0.8rem;
    margin-top: 0.1rem;
  }
`;

const InvestmentBadge = styled.div`
  display: inline-block;
  background: rgba(37, 99, 235, 0.1);
  color: ${corporateTheme.primary};
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  border: 1px solid rgba(37, 99, 235, 0.3);
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Timeline = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const timelineData = [
    {
      phase: "Development & Setup",
      date: "Months 1-6",
      icon: corporateLabels.development,
      color: corporateTheme.primaryGradient,
      investment: "€8M Investment",
      description: "Foundation phase focused on platform development, team building, and initial content creation infrastructure.",
      milestones: [
        "Platform development and testing",
        "Core team recruitment and training",
        "Technology infrastructure setup",
        "Initial content website development",
        "Legal and compliance framework establishment",
        "Regional market research completion"
      ]
    },
    {
      phase: "Launch & Initial Operations",
      date: "Months 7-12",
      icon: corporateLabels.launch,  
      color: `linear-gradient(135deg, ${corporateTheme.primary} 0%, ${corporateTheme.primaryLight} 100%)`,
      investment: "€2M Additional",
      description: "Launch phase with first revenue streams from early content websites and initial advertiser partnerships.",
      milestones: [
        "Platform beta launch with select advertisers",
        "First batch of content websites goes live",
        "Initial affiliate recruitment campaign",
        "Regional office establishments",
        "First revenue streams commence",
        "Performance optimization and feedback integration"
      ]
    },
    {
      phase: "Growth & Expansion",
      date: "Months 13-24",
      icon: corporateLabels.growth,
      color: `linear-gradient(135deg, ${corporateTheme.primaryLight} 0%, ${corporateTheme.accent} 100%)`,
      investment: "€1.5M Scaling",
      description: "Accelerated growth with expanded content portfolio and increased advertiser/affiliate network.",
      milestones: [
        "Full platform launch across all target markets",
        "Content website portfolio reaches 50+ sites",
        "Major advertiser partnerships established",
        "Affiliate network scales to 1000+ members",
        "SEO traffic significantly increases",
        "Path to profitability clearly defined"
      ]
    },
    {
      phase: "Scale & Optimization",
      date: "Year 3+",
      icon: corporateLabels.optimization,
      color: `linear-gradient(135deg, ${corporateTheme.accent} 0%, ${corporateTheme.success} 100%)`,
      investment: "Self-Funded Growth",
      description: "Market leadership phase with strong profitability and expansion into new verticals and regions.",
      milestones: [
        "Market leadership position achieved",
        "Strong profitability and positive cash flow",
        "Content websites generate significant organic traffic",
        "Expansion into new verticals and sub-regions",
        "Advanced AI and automation implementation",
        "Potential exit opportunities or further expansion"
      ]
    }
  ];

  return (
    <TimelineContainer ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Title variants={itemVariants}>
          Implementation Timeline
        </Title>

        <TimelineWrapper>
          <TimelineLine />
          
          {timelineData.map((item, index) => (
            <TimelineItem key={index} variants={itemVariants}>
              <TimelineDot
                color={item.color}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.3 }}
              >
                {item.icon}
              </TimelineDot>
              
              <TimelineContent
                whileHover={{ scale: 1.02, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <InvestmentBadge>{item.investment}</InvestmentBadge>
                <PhaseTitle>{item.phase}</PhaseTitle>
                <PhaseDate>{item.date}</PhaseDate>
                <PhaseDescription>{item.description}</PhaseDescription>
                <MilestoneList>
                  {item.milestones.map((milestone, milestoneIndex) => (
                    <Milestone key={milestoneIndex}>{milestone}</Milestone>
                  ))}
                </MilestoneList>
              </TimelineContent>
            </TimelineItem>
          ))}
        </TimelineWrapper>
      </motion.div>
    </TimelineContainer>
  );
};

export default Timeline; 